
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function Hero() {
  const [scrolled, setScrolled] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(true);
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToContact = () => {
    document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const navItems = [
    { label: '關於我', path: '/about' },
    { label: '技能', path: '/skills' },
    { label: '經歷', path: '/experience' },
    { label: '個人專案', path: '/side-projects' },
  ];

  const stats = [
    { value: '2.5+', label: '法律工作年資（年）' },
    { value: '795', label: 'TOEIC 多益分數' },
    { value: '100+', label: '協助法律案件' },
  ];

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 flex flex-col">

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 flex items-center justify-between">
          <Link
            to="/"
            className={`text-2xl font-bold transition-all duration-300 cursor-pointer text-gray-900 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Frances
          </Link>
          <div className="hidden md:flex items-center gap-12">
            {navItems.map((item, index) => (
              <Link
                key={item.label}
                to={item.path}
                className={`text-sm font-medium text-gray-700 transition-all duration-300 hover:text-orange-600 cursor-pointer relative group ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}
                style={{ transitionDelay: `${(index + 1) * 100}ms` }}
              >
                {item.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-orange-500 to-rose-500 group-hover:w-full transition-all duration-300"></span>
              </Link>
            ))}
            <button
              onClick={scrollToContact}
              className={`px-8 py-3 rounded-full text-sm font-semibold bg-gradient-to-r from-orange-500 to-rose-500 text-white hover:shadow-lg hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}
              style={{ transitionDelay: '500ms' }}
            >
              聯絡我
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Content — full width centered */}
      <div className="flex-1 flex items-center justify-center px-4 md:px-8 pt-24 pb-40">
        <div className="max-w-4xl w-full text-center">

          {/* Badge */}
          <div
            className={`inline-flex items-center gap-2 px-4 py-2 bg-orange-100 rounded-full mb-8 transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '100ms' }}
          >
            <span className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></span>
            <span className="text-sm font-semibold text-orange-700">積極轉職中・數據分析師 / 數據工程師</span>
          </div>

          {/* Main heading */}
          <h1
            className={`font-bold text-gray-900 leading-tight mb-6 transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(3rem, 8vw, 6.5rem)', transitionDelay: '200ms' }}
          >
            I'm{' '}
            <span className="bg-gradient-to-r from-orange-500 to-rose-500 bg-clip-text text-transparent">
              Frances Shen
            </span>
          </h1>

          {/* Subtitle */}
          <p
            className={`text-xl text-gray-500 leading-relaxed mb-4 transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '300ms' }}
          >
            法律背景出身・轉型數據領域
          </p>
          <p
            className={`text-base text-gray-500 max-w-2xl mx-auto leading-relaxed mb-6 transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '380ms' }}
          >
            擁有 2 年半以上律師事務所工作經驗，具備法律合規與文件分析背景，<br />
            目前積極精進 Python、SQL 與資料視覺化技能，邁向數據分析師／工程師之路。
          </p>

          {/* Slogan */}
          <div
            className={`inline-flex items-center gap-3 px-6 py-3 bg-white/70 backdrop-blur-sm border border-orange-200 rounded-2xl mb-10 transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '430ms' }}
          >
            <i className="ri-double-quotes-l text-orange-400 text-xl flex-shrink-0"></i>
            <p className="text-sm text-gray-600 leading-relaxed">
              數據分析和法律一樣，都是<strong className="text-gray-900 font-bold">整理資料的一套技術</strong>。
            </p>
          </div>

          {/* Buttons */}
          <div
            className={`flex items-center justify-center gap-5 mb-20 transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '450ms' }}
          >
            <Link
              to="/side-projects"
              className="px-10 py-4 rounded-full font-semibold text-base bg-gradient-to-r from-orange-500 to-rose-500 text-white hover:shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              查看我的專案
            </Link>
            <Link
              to="/about"
              className="px-10 py-4 rounded-full font-semibold text-base border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              了解更多
            </Link>
          </div>

          {/* Stats */}
          <div
            className={`flex flex-wrap items-center justify-center gap-8 md:gap-16 transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '550ms' }}
          >
            {stats.map((stat, index) => (
              <div key={index} className="text-center group">
                <div
                  className="text-4xl font-bold text-gray-900 mb-1 group-hover:text-orange-600 transition-colors duration-300"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {stat.value}
                </div>
                <div className="text-sm text-gray-500">{stat.label}</div>
              </div>
            ))}
          </div>

        </div>
      </div>

      {/* Wave Divider */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path d="M0 0L60 10C120 20 240 40 360 46.7C480 53 600 47 720 43.3C840 40 960 40 1080 46.7C1200 53 1320 67 1380 73.3L1440 80V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V0Z" fill="white" />
        </svg>
      </div>

    </div>
  );
}
